/**
 * State and rules of the handwriting pad, as pure functions so they can be
 * tested without a browser. HandwritingPad.tsx only wires these to pointer
 * events, timers and the keyboard.
 *
 * - Ink: strokes (pad units) → live recognition → one chip per character.
 * - Correction: tapping a chip cycles through that character's candidates;
 *   a chip the player touched counts as confirmed (confidence 1).
 * - Watch layout ("compact"): characters are written one at a time and
 *   appended to an answer line after a short pause (`commit`).
 * - Keyboard: typed characters build the answer directly (`via: 'kb'`).
 * - Submit rules (docs/HANDWRITING.md § Fallback): see decideSubmit.
 */
import { type Rational, parseAnswer } from '@dropstack/core';
import type { HwCandidate, HwChar, HwResult, HwStroke } from '../contracts.ts';
import { charConfidence } from './recognizer/classify.ts';
import { bestValidPicks } from './recognizer/decode.ts';
import { isHwChar } from './recognizer/templates.ts';

export const PAD = {
  /** Live recognition runs this long after a stroke ends (starting a new stroke postpones it). */
  RECOGNIZE_DELAY_MS: 150,
  /** Watch layout: idle time after the last stroke before the character joins the answer line. */
  COMPACT_COMMIT_MS: 700,
  /** Submitting a reading below this confidence hands the problem to multiple choice. */
  MIN_SUBMIT_CONFIDENCE: 0.2,
  /** Submits that don't read as a number, per problem, before falling back to multiple choice. */
  MAX_FAILED_SUBMITS: 2,
  /** Chips below this confidence are drawn as "unsure" to invite a check. */
  UNSURE_CONFIDENCE: 0.45,
  /** Longest answer the keyboard accepts. */
  MAX_TYPED: 12,
} as const;

/** Why the pad is talking to the player (rendered through the 'hw' strings). */
export type PadMessage =
  | { kind: 'empty' }
  | { kind: 'notNumber' }
  | { kind: 'unreadable' }
  | { kind: 'suggest'; text: string }
  | { kind: 'fallback' };

/** One recognised character before the player's picks are applied. */
export interface ChipSource {
  /** Stable identity: `s:<stroke indices>` for ink on the canvas, `c:<n>` for committed characters. */
  key: string;
  /** Best first. */
  candidates: readonly HwCandidate[];
}

export interface PadState {
  /** Ink on the canvas, in pad units. */
  strokes: HwStroke[];
  /** Bumped on every ink change, so a recognition result for older ink can be recognised as stale. */
  inkVersion: number;
  /** Latest recognition of the ink, made at `resultVersion`. */
  result: HwResult | null;
  resultVersion: number;
  /** Watch layout: characters already appended to the answer line. */
  committed: ChipSource[];
  /** Candidate index the player chose or accepted, per chip key. Keys present = confirmed chips. */
  picks: Readonly<Record<string, number>>;
  /** Keyboard answer (non-empty = keyboard mode; ink and keyboard never mix). */
  typed: string;
  /** Submits this problem that didn't read as a number. */
  failures: number;
  message: PadMessage | null;
  /** Next id for committed chips. */
  serial: number;
}

export const initialPadState = (): PadState => ({
  strokes: [],
  inkVersion: 0,
  result: null,
  resultVersion: -1,
  committed: [],
  picks: {},
  typed: '',
  failures: 0,
  message: null,
  serial: 0,
});

export type PadAction =
  /** New problem: forget everything. */
  | { type: 'reset' }
  | { type: 'strokeAdded'; stroke: HwStroke }
  /** Remove the last stroke; with no ink, the last committed character or typed character. */
  | { type: 'undo' }
  | { type: 'clear' }
  | { type: 'recognized'; version: number; result: HwResult }
  /** Watch layout: append the recognised ink to the answer line and clear the canvas. */
  | { type: 'commit'; version: number; result: HwResult }
  /** Tap on a chip: next candidate. */
  | { type: 'cycle'; key: string }
  /** One typed character (already filtered by keyToAnswerChar). */
  | { type: 'typed'; char: HwChar }
  | { type: 'backspace' };

export const strokeKey = (strokeIndices: readonly number[]): string => `s:${strokeIndices.join('.')}`;

const keyUsesStroke = (key: string, index: number): boolean =>
  key.startsWith('s:') && key.slice(2).split('.').includes(String(index));

const liveSources = (result: HwResult | null): ChipSource[] =>
  (result?.chars ?? []).map((c) => ({ key: strokeKey(c.strokeIndices), candidates: c.candidates }));

const withoutKeys = (picks: Readonly<Record<string, number>>, drop: (key: string) => boolean): Record<string, number> =>
  Object.fromEntries(Object.entries(picks).filter(([k]) => !drop(k)));

const clearedInk = (s: PadState): PadState => ({
  ...s,
  strokes: [],
  inkVersion: s.inkVersion + 1,
  result: null,
  committed: [],
  picks: {},
  message: null,
});

export function padReducer(s: PadState, a: PadAction): PadState {
  switch (a.type) {
    case 'reset':
      return { ...initialPadState(), inkVersion: s.inkVersion + 1, serial: s.serial };

    case 'strokeAdded':
      if (!a.stroke.length) return s;
      return { ...s, strokes: [...s.strokes, a.stroke], inkVersion: s.inkVersion + 1, typed: '', message: null };

    case 'undo': {
      if (s.strokes.length) {
        const removed = s.strokes.length - 1;
        const strokes = s.strokes.slice(0, removed);
        return {
          ...s,
          strokes,
          inkVersion: s.inkVersion + 1,
          result: strokes.length ? s.result : null,
          picks: withoutKeys(s.picks, (k) => keyUsesStroke(k, removed)),
          message: null,
        };
      }
      const last = s.committed[s.committed.length - 1];
      if (last) {
        return { ...s, committed: s.committed.slice(0, -1), picks: withoutKeys(s.picks, (k) => k === last.key), message: null };
      }
      return s.typed ? { ...s, typed: s.typed.slice(0, -1), message: null } : s;
    }

    case 'clear':
      return { ...clearedInk(s), typed: '' };

    case 'recognized': {
      if (a.version !== s.inkVersion) return s;
      const present = new Set(liveSources(a.result).map((c) => c.key));
      return {
        ...s,
        result: a.result,
        resultVersion: a.version,
        picks: withoutKeys(s.picks, (k) => k.startsWith('s:') && !present.has(k)),
      };
    }

    case 'commit': {
      if (a.version !== s.inkVersion) return s;
      let serial = s.serial;
      const picks: Record<string, number> = withoutKeys(s.picks, (k) => k.startsWith('s:'));
      const added = liveSources(a.result).map((src) => {
        const key = `c:${serial++}`;
        const pick = s.picks[src.key];
        if (pick !== undefined) picks[key] = pick;
        return { key, candidates: src.candidates };
      });
      return {
        ...s,
        strokes: [],
        inkVersion: s.inkVersion + 1,
        result: null,
        committed: [...s.committed, ...added],
        picks,
        serial,
      };
    }

    case 'cycle': {
      const src = [...s.committed, ...liveSources(s.result)].find((c) => c.key === a.key);
      if (!src || src.candidates.length < 2) return s;
      const next = ((s.picks[a.key] ?? 0) + 1) % src.candidates.length;
      return { ...s, picks: { ...s.picks, [a.key]: next }, message: null };
    }

    case 'typed': {
      if (s.typed.length >= PAD.MAX_TYPED) return s;
      const base = s.strokes.length || s.committed.length ? clearedInk(s) : s;
      return { ...base, typed: s.typed + a.char, message: null };
    }

    case 'backspace':
      return s.typed ? { ...s, typed: s.typed.slice(0, -1), message: null } : padReducer(s, { type: 'undo' });
  }
}

/** Map a KeyboardEvent.key to an answer character (decimal comma and typographic minus accepted). */
export function keyToAnswerChar(key: string): HwChar | null {
  const k = key === ',' ? '.' : key === '−' || key === '–' ? '-' : key;
  return isHwChar(k) ? k : null;
}

// ───────────── Reading ─────────────

export interface ChipView {
  key: string;
  /** The character currently shown (the picked candidate). */
  char: HwChar;
  candidates: readonly HwCandidate[];
  pick: number;
  /** The player tapped this chip (or accepted a suggestion that included it). */
  confirmed: boolean;
  /** 0–1; 1 for confirmed chips. */
  confidence: number;
  /** Watch layout: still on the canvas, not yet appended to the answer line. */
  pending: boolean;
}

export interface Reading {
  source: 'ink' | 'keyboard' | 'none';
  text: string;
  value: Rational | null;
  /** Min over chips; 1 for typed answers, 0 when there is nothing. */
  confidence: number;
  chips: ChipView[];
}

function viewOf(src: ChipSource, picks: Readonly<Record<string, number>>, pending: boolean): ChipView | null {
  const stored = picks[src.key];
  const pick = stored !== undefined && stored < src.candidates.length ? stored : 0;
  const chosen = src.candidates[pick];
  if (!chosen) return null;
  const confirmed = stored !== undefined;
  return {
    key: src.key,
    char: chosen.char,
    candidates: src.candidates,
    pick,
    confirmed,
    confidence: confirmed ? 1 : charConfidence(src.candidates),
    pending,
  };
}

/** What the pad currently reads (committed characters first, then the ink on the canvas). */
export function readingOf(s: PadState, compact = false): Reading {
  if (s.typed) return { source: 'keyboard', text: s.typed, value: parseAnswer(s.typed), confidence: 1, chips: [] };
  const chips = [
    ...s.committed.map((c) => viewOf(c, s.picks, false)),
    ...liveSources(s.result).map((c) => viewOf(c, s.picks, compact)),
  ].filter((c) => c !== null);
  if (!chips.length) return { source: 'none', text: '', value: null, confidence: 0, chips: [] };
  const text = chips.map((c) => c.char).join('');
  return {
    source: 'ink',
    text,
    value: parseAnswer(text),
    confidence: Math.min(...chips.map((c) => c.confidence)),
    chips,
  };
}

/**
 * Make sure the reading reflects all the ink before acting on it: recognise
 * stale ink now, and in the Watch layout append any pending character.
 */
export function freshen(s: PadState, compact: boolean, recognize: (strokes: HwStroke[]) => HwResult): PadState {
  if (!s.strokes.length) return s;
  const current = s.result && s.resultVersion === s.inkVersion ? s.result : null;
  const fresh = current ?? recognize(s.strokes);
  const withResult = current ? s : padReducer(s, { type: 'recognized', version: s.inkVersion, result: fresh });
  return compact ? padReducer(withResult, { type: 'commit', version: s.inkVersion, result: fresh }) : withResult;
}

// ───────────── Submit ─────────────

export type SubmitEffect = { kind: 'submit'; text: string; via: 'hw' | 'kb' } | { kind: 'fallback' };

/**
 * The best valid reading using the chips' other candidates, as picks for
 * every chip (so accepting it confirms the whole answer), or null.
 */
export function suggestFix(chips: readonly ChipView[]): { picks: Record<string, number>; text: string } | null {
  const picks = bestValidPicks(
    chips.map((c) => c.candidates),
    chips.map((c) => (c.confirmed ? c.pick : null)),
  );
  if (!picks) return null;
  const out: Record<string, number> = {};
  chips.forEach((c, i) => (out[c.key] = picks[i]!));
  return { picks: out, text: chips.map((c, i) => c.candidates[picks[i]!]!.char).join('') };
}

/**
 * Decide what a press of ✓ (or Enter) does. Expects a freshened state.
 *
 * - Typed answer: sent as `kb` if it is a number, otherwise a gentle nudge.
 * - Nothing written: nudge.
 * - Ink that doesn't read as a number: counts as a failed attempt. The first
 *   time, the pad suggests the closest valid reading (if any) for the player
 *   to accept with a second ✓, or asks them to rewrite. The second failure
 *   falls back to multiple choice.
 * - A valid number read with confidence under MIN_SUBMIT_CONFIDENCE falls
 *   back straight away: it is more likely a misreading than the player's
 *   answer, and a misreading would cost them a brick layer.
 * - Otherwise the reading is sent as `hw`.
 */
export function decideSubmit(s: PadState, compact = false): { state: PadState; effect: SubmitEffect | null } {
  const r = readingOf(s, compact);
  const say = (message: PadMessage, patch: Partial<PadState> = {}) => ({ state: { ...s, ...patch, message }, effect: null });
  const fallback = (failures: number) => ({
    state: { ...s, failures, message: { kind: 'fallback' } as const },
    effect: { kind: 'fallback' } as const,
  });

  if (r.source === 'keyboard') {
    return r.value ? { state: { ...s, message: null }, effect: { kind: 'submit', text: r.text, via: 'kb' } } : say({ kind: 'notNumber' });
  }
  if (r.source === 'none') return say({ kind: 'empty' });

  if (r.value === null) {
    const failures = s.failures + 1;
    if (failures >= PAD.MAX_FAILED_SUBMITS) return fallback(failures);
    const fix = suggestFix(r.chips);
    return fix
      ? say({ kind: 'suggest', text: fix.text }, { failures, picks: { ...s.picks, ...fix.picks } })
      : say({ kind: 'unreadable' }, { failures });
  }
  if (r.confidence < PAD.MIN_SUBMIT_CONFIDENCE) return fallback(s.failures);
  return { state: { ...s, message: null }, effect: { kind: 'submit', text: r.text, via: 'hw' } };
}
